import { PREFERENCE, readPreference, writePreference } from './prefs'
import { useCallback, useEffect, useState } from 'react'
import file from '../data/palettes.json'

/**
 * The outcome colour scheme.
 *
 * Applied by rewriting the same custom properties the stylesheet already uses,
 * so charts, badges, meters and the summary band all follow without any of them
 * knowing a preference exists.
 *
 * Changing it is a preference, never a correctness matter: every outcome in
 * this interface carries a glyph and a word as well as a colour, so a scheme
 * that someone cannot distinguish still leaves the data readable.
 */

export interface PaletteScheme {
  id: string
  name: string
  description: string
  light: { pass: string; fail: string; conflict: string }
  dark: { pass: string; fail: string; conflict: string }
}

export const PALETTES = (file as { palettes: PaletteScheme[] }).palettes

export const DEFAULT_PALETTE = 'default'

function paletteById(id: string): PaletteScheme {
  return PALETTES.find((scheme) => scheme.id === id) ?? PALETTES[0]!
}

/**
 * Derives the quiet background that pairs with each outcome colour.
 *
 * Mixing against the surface rather than shipping a second set of values means
 * a new scheme needs three colours, not six, and the backgrounds cannot drift
 * out of step with the foregrounds.
 */
function apply(scheme: PaletteScheme): void {
  const dark = document.documentElement.dataset.theme === 'dark'
  const colours = dark ? scheme.dark : scheme.light
  const root = document.documentElement.style

  for (const [state, colour] of Object.entries(colours)) {
    root.setProperty(`--${state}`, colour)
    root.setProperty(
      `--${state}-quiet`,
      `color-mix(in srgb, ${colour} ${dark ? '18%' : '12%'}, var(--surface))`,
    )
  }
}

export function usePalette(): [string, (id: string) => void, PaletteScheme] {
  const [id, setId] = useState<string>(() => readPreference(PREFERENCE.palette) ?? DEFAULT_PALETTE)

  useEffect(() => {
    apply(paletteById(id))

    // The scheme has separate values per theme, so it has to be reapplied when
    // the theme changes underneath it.
    const observer = new MutationObserver(() => apply(paletteById(id)))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [id])

  const set = useCallback((next: string) => {
    setId(next)
    writePreference(PREFERENCE.palette, next)
  }, [])

  return [id, set, paletteById(id)]
}
